import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Radio, Square, UserPlus, X, Youtube } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { StudioOverlayPanel } from "@/components/studio/StudioOverlayPanel";

const field = "w-full rounded-xl border border-[#E5E0D8] bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#144F75]";
const LAYOUTS = [["grid", "Grid — everyone on screen"], ["dominant", "Speaker — large speaker, others beside"], ["split", "Side by side — first two people"], ["single", "Solo — one person full screen"]];

export const StudioHostBar = ({ eid, info, refresh, watchPath, health }) => {
  const [email, setEmail] = useState("");
  const [layout, setLayout] = useState(info.layout || "grid");
  const [busy, setBusy] = useState("");
  const run = async (key, fn, msg) => {
    setBusy(key);
    try { await fn(); if (msg) toast.success(msg); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Something went wrong"); }
    finally { setBusy(""); }
  };
  const connectYouTube = async () => {
    try { await api.post(`/events/${eid}/stream/youtube`); }
    catch (e) {
      const st = e.response?.status;
      if (st === 401 || /connect|reconnect/i.test(String(e.response?.data?.detail || ""))) {
        const r = await api.get("/youtube/live/connect");
        window.location.href = r.data.url;
        return;
      }
      throw e;
    }
  };
  const invite = (e) => { e.preventDefault(); if (email.trim()) run("inv", () => api.post(`/events/${eid}/studio/guests`, { email }).then(() => setEmail("")), "Guest invited — they'll get a notification"); };
  const live = info.status === "live";
  return (
    <aside className="eyw-card p-5 space-y-5" data-testid="studio-host-bar">
      <div>
        <h2 className="font-serif text-xl text-[#1A242B]">Broadcast</h2>
        <p className="text-xs text-[#8FA89B]">You're backstage until you go live. Viewers watch on the <Link to={watchPath} className="underline" data-testid="studio-watch-link">watch page</Link>.</p>
      </div>
      {!info.youtube_ready && (
        <div className="text-xs rounded-lg bg-[#F5F1EA] text-[#4A5D6B] p-3 space-y-2" data-testid="studio-youtube-missing">
          <p>Viewers watch through YouTube Live. Connect it once and a broadcast is created for this event.</p>
          <button type="button" disabled={!!busy} onClick={() => run("yt", connectYouTube, "YouTube Live broadcast ready")} data-testid="studio-connect-youtube" className="eyw-btn !py-1.5 text-xs w-full justify-center"><Youtube size={14} /> Connect YouTube Live</button>
        </div>
      )}
      {live && health?.alert && <p role="alert" className="text-sm rounded-lg bg-red-50 border border-red-200 text-red-700 p-3" data-testid="studio-health-alert">{health.alert}</p>}
      {live && health && !health.alert && <p className="text-xs text-[#4A5D6B]" data-testid="studio-health-ok">{health.confirmed ? "YouTube confirms your stream is live." : "Waiting for YouTube to confirm the stream…"}</p>}
      <div className="space-y-2">
        <label htmlFor="studio-layout" className="text-xs text-[#4A5D6B]">Layout viewers see</label>
        <select id="studio-layout" value={layout} disabled={!!busy} data-testid="studio-layout-select" className={field}
          onChange={(e) => { const v = e.target.value; setLayout(v); if (live) run("lay", () => api.post(`/events/${eid}/studio/live/overlay`, { layout: v }), "Layout changed"); }}>
          {LAYOUTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>
      {live ? (
        <button disabled={!!busy} onClick={() => window.confirm("End the broadcast for everyone?") && run("stop", () => api.post(`/events/${eid}/studio/live/stop`), "Broadcast ended")} data-testid="studio-stop-live" className="eyw-btn-outline w-full justify-center"><Square size={15} /> End broadcast</button>
      ) : (
        <button disabled={!!busy || !info.youtube_ready || info.status === "ended"} onClick={() => run("live", () => api.post(`/events/${eid}/studio/live/start`, { layout }), "You're live on YouTube")} data-testid="studio-go-live" className="eyw-btn w-full justify-center !bg-red-600 disabled:opacity-50"><Radio size={15} /> {busy === "live" ? "Starting…" : "Go live"}</button>
      )}
      <StudioOverlayPanel eid={eid} info={info} refresh={refresh} />
      <div className="space-y-2">
        <p className="text-xs text-[#4A5D6B]">Guests ({info.guests?.length || 0}/{info.max_on_screen - 1}) — they need an account</p>
        <form onSubmit={invite} className="flex gap-2">
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="guest@email.com" aria-label="Guest email" data-testid="studio-guest-email" className={field} />
          <button type="submit" disabled={busy === "inv"} data-testid="studio-guest-invite" aria-label="Invite guest" className="eyw-btn !px-3 !py-2"><UserPlus size={15} /></button>
        </form>
        <ul className="space-y-1" data-testid="studio-guest-list">
          {(info.guests || []).map((g) => (
            <li key={g.id} className="flex items-center justify-between text-sm" data-testid={`studio-guest-${g.id}`}>
              <span className="truncate">{g.name || g.email}</span>
              <button onClick={() => run(`rm-${g.id}`, () => api.delete(`/events/${eid}/studio/guests/${g.id}`), "Invitation removed")} aria-label={`Remove invitation for ${g.name || g.email}`} data-testid={`studio-guest-remove-${g.id}`} className="text-[#8FA89B] hover:text-red-600"><X size={14} /></button>
            </li>
          ))}
        </ul>
      </div>
    </aside>
  );
};
